/**
 * Ted #27 part 2 (#988) — the pure half of "Outstanding — past, not
 * completed": how a past-dated open reservation is READ and what check-out
 * time it proposes. Kept out of `Bookings.tsx` so it can be pinned by unit
 * tests without rendering the whole money path.
 *
 * Ted, 2026-09-15 (Journey & Solar, an 2026-08-29 daycare still open):
 * *"Reservations that are still open or awaiting checkout in Gingr need to
 * be identifiable in Animal Lovers, even if the original reservation date
 * has passed. Staff needs a way to locate and complete these outstanding
 * transactions."*
 *
 * The rule that matters here: **completing one of these must charge the day
 * the service happened, not today.** That is Ted #6 / #909 (the bounded
 * `checked_out_at` override, already in prod via #928) — this module decides
 * what to put in that field so the desk does not have to compute it.
 */
import type { AdminBooking } from './api.js';

/** Default wall-clock time proposed for a forgotten check-out (5:00 PM). */
export const OUTSTANDING_DEFAULT_PICKUP = '17:00';

/** Default wall-clock time proposed for a forgotten ARRIVAL (8:00 AM). */
export const OUTSTANDING_DEFAULT_ARRIVAL = '08:00';

/**
 * The booking's LAST service day as `YYYY-MM-DD`.
 *
 * `end_date` wins over `start_date` wins over `date`, the same precedence
 * `day-semantics.ts` uses server-side (a multi-night stay's outstanding day
 * is the day it should have ended). Date columns arrive as UTC-midnight ISO
 * strings, so the calendar date is the first ten characters — parsing them
 * into a local `Date` first is what shifts a booking to the previous day for
 * anyone west of UTC.
 */
export function lastServiceDayIso(
  b: Pick<AdminBooking, 'date' | 'start_date' | 'end_date'>,
): string {
  const raw = b.end_date ?? b.start_date ?? b.date;
  return String(raw).slice(0, 10);
}

/**
 * The booking's FIRST service day as `YYYY-MM-DD` (`start_date`, else
 * `date`). Same string-slice discipline as `lastServiceDayIso`.
 *
 * The pair is the module's whole answer to "which day": a visit ARRIVES on
 * its first service day and DEPARTS on its last. Kept adjacent because the
 * 09-16 re-review's minor was exactly a disagreement between them — the
 * arrival proposal was reading the LAST day while the arrival's own `min`
 * bound read the first, so a forgotten six-night boarding stay was proposed
 * for check-in on the morning it ended.
 */
export function firstServiceDayIso(
  b: Pick<AdminBooking, 'date' | 'start_date'>,
): string {
  const raw = b.start_date ?? b.date;
  return String(raw).slice(0, 10);
}

/**
 * The `datetime-local` value to pre-fill the corrected check-out field with:
 * the booking's last service day at its own pick-up time when the customer
 * asked for one, otherwise 5:00 PM.
 *
 * It is a PROPOSAL, not a claim — the field stays editable, and the server
 * still bounds it (never in the future, never before the check-in). What the
 * proposal guarantees is the part the desk cannot be asked to remember at
 * 6:45 AM: the DAY. A visit found three weeks late is charged to the day it
 * happened.
 *
 * Returns `''` for a row with no usable date, so the caller pre-fills
 * nothing rather than an invented instant.
 */
export function outstandingCheckoutDefault(
  b: Pick<AdminBooking, 'date' | 'start_date' | 'end_date' | 'pick_up_time'>,
): string {
  const day = lastServiceDayIso(b);
  if (!/^\d{4}-\d{2}-\d{2}$/.test(day)) return '';
  const raw = (b.pick_up_time ?? '').trim();
  const time = /^\d{2}:\d{2}/.test(raw) ? raw.slice(0, 5) : OUTSTANDING_DEFAULT_PICKUP;
  return `${day}T${time}`;
}

/**
 * The `datetime-local` value to pre-fill the corrected ARRIVAL field with:
 * the booking's FIRST service day at its own drop-off time when the
 * customer asked for one, otherwise 8:00 AM.
 *
 * WHY THIS EXISTS AT ALL — the 09-16 review's first major. 81 of HTHD's 111
 * outstanding rows are `pending` or `confirmed`, so completing one starts
 * with a check-in. Check-in used to stamp `checked_in_at = NOW()`, and
 * `checkOutBooking` refuses any `checked_out_at` earlier than the check-in
 * (422 `checkout_time_invalid` / `before_check_in`). The pre-filled
 * original-date check-out was therefore GUARANTEED to fail on those rows,
 * and the only way through was to overwrite it with today — putting the
 * charge on the wrong day, the exact bug Ted #27 part 2 exists to fix.
 *
 * THE FIRST DAY, NOT THE LAST — the 09-16 re-review's minor. This read
 * `lastServiceDayIso` to stay "the same day as the check-out proposal", and
 * that is only true of a single-day visit. A forgotten six-night boarding
 * stay (05–11 Sep) was proposed for check-in at 07:15 on the 11th: the
 * server accepts it (its floor is first-day-minus-one, `checkInFloorFor`),
 * writes a `check_in_time_corrected` audit row, and the stay then reads as
 * arriving and departing on its LAST day in every `checked_in_at` report.
 * The module was also contradicting itself — `outstandingArrivalMin` has
 * always bounded this field by the FIRST day.
 *
 * Still internally consistent with the check-out proposal, and now for the
 * right reason: first day ≤ last day, and on a single-day visit 8:00 AM is
 * earlier than the 5:00 PM pick-up.
 */
export function outstandingCheckinDefault(
  b: Pick<AdminBooking, 'date' | 'start_date' | 'end_date' | 'drop_off_time'>,
): string {
  const day = firstServiceDayIso(b);
  if (!/^\d{4}-\d{2}-\d{2}$/.test(day)) return '';
  const raw = (b.drop_off_time ?? '').trim();
  const time = /^\d{2}:\d{2}/.test(raw) ? raw.slice(0, 5) : OUTSTANDING_DEFAULT_ARRIVAL;
  return `${day}T${time}`;
}

/**
 * How overdue the visit is, in whole days, measured against the facility day
 * the server reported (`before`). Used for the row's plain-language age —
 * "28 days ago" reads; an ISO timestamp does not (anti-pattern list: raw
 * identifiers and ISO timestamps shown to users).
 */
export function daysOverdue(lastDay: string, facilityToday: string): number | null {
  if (!/^\d{4}-\d{2}-\d{2}$/.test(lastDay) || !/^\d{4}-\d{2}-\d{2}$/.test(facilityToday)) {
    return null;
  }
  const a = Date.parse(`${lastDay}T00:00:00.000Z`);
  const b = Date.parse(`${facilityToday}T00:00:00.000Z`);
  if (Number.isNaN(a) || Number.isNaN(b)) return null;
  return Math.round((b - a) / 86_400_000);
}

/** "2026-08-29" → "Aug 29, 2026" (never a raw ISO string on screen). */
export function formatServiceDay(iso: string): string {
  if (!/^\d{4}-\d{2}-\d{2}$/.test(iso)) return iso;
  const d = new Date(`${iso}T12:00:00.000Z`);
  return d.toLocaleDateString(undefined, {
    timeZone: 'UTC',
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });
}

/**
 * The row's one-line provenance: where the reservation came from and, for a
 * Gingr mirror, the id the desk types into Gingr. Ted checks the visit out
 * in Gingr FIRST (the 06:00 MT sync rewrites status for 30 days, #989), so
 * this id is operationally load-bearing, not decoration.
 */
export function sourceLabel(
  b: Pick<AdminBooking, 'gingr_reservation_id' | 'booking_source'>,
): string {
  const gingrId = b.gingr_reservation_id;
  if (gingrId) return `Gingr #${gingrId}`;
  const source = b.booking_source ?? null;
  if (source === 'staff') return 'Booked at the desk';
  if (source === 'customer') return 'Booked by the customer';
  return 'Animal Lovers';
}

/**
 * Pre-fill the corrected-check-out field for every outstanding row that does
 * not already have one, WITHOUT clobbering anything the desk has typed.
 *
 * This is the mechanism behind "Check Out from here charges the day the
 * service happened": the field the row already had (Ted #6 / #909, in prod
 * via #928) arrives pre-filled with the visit's own day instead of blank,
 * and the existing check-out path sends it as `checked_out_at`.
 */
export function seedOutstandingCheckoutTimes(
  prev: Readonly<Record<string, string>>,
  rows: ReadonlyArray<
    Pick<AdminBooking, 'id' | 'date' | 'start_date' | 'end_date' | 'pick_up_time'>
  >,
): Record<string, string> {
  const next = { ...prev };
  for (const row of rows) {
    if (next[row.id] !== undefined) continue;
    const proposed = outstandingCheckoutDefault(row);
    if (proposed) next[row.id] = proposed;
  }
  return next;
}

/**
 * The arrival half of the same mechanism, for the rows that have not been
 * checked in yet. Same no-clobber rule.
 */
export function seedOutstandingCheckinTimes(
  prev: Readonly<Record<string, string>>,
  rows: ReadonlyArray<
    Pick<AdminBooking, 'id' | 'status' | 'date' | 'start_date' | 'end_date' | 'drop_off_time'>
  >,
): Record<string, string> {
  const next = { ...prev };
  for (const row of rows) {
    // Only a row the desk still has to check IN needs an arrival proposal.
    if (row.status !== 'pending' && row.status !== 'confirmed') continue;
    if (next[row.id] !== undefined) continue;
    const proposed = outstandingCheckinDefault(row);
    if (proposed) next[row.id] = proposed;
  }
  return next;
}

// ── What is on screen is what is sent ───────────────────────────────────

/** The two Bookings views. Mirrors `BookingsView` in `Bookings.tsx`. */
export type OutstandingView = 'day' | 'outstanding';

/**
 * Is the corrected-ARRIVAL control on screen for a row in this view?
 *
 * 09-16 re-review, the major: the seeded `checkinTimeOverride` /
 * `checkoutTimeOverride` maps survived a view switch, and the submit paths
 * read them with NO view guard — so the DAY list posted a backdated
 * `checked_in_at` (and, for a grooming row, a backdated `checked_out_at`)
 * with no field on screen to see or correct it. That is a money-dated write
 * the desk cannot see, which is exactly the Counter Test failure the
 * grooming half of this PR had already fixed once.
 *
 * The fix is structural rather than a second `view === 'outstanding'`
 * sprinkled at the call site: the render and the submit path ask the SAME
 * function, so a field that is not drawn cannot be sent. Clearing the maps
 * on a view change (Bookings.tsx) removes the stale value; this removes the
 * whole class.
 */
export function correctedArrivalVisible(view: OutstandingView): boolean {
  return view === 'outstanding';
}

/**
 * Is the corrected-CHECK-OUT control on screen for a row in this view?
 *
 * Every non-grooming row has carried it since Ted #6 / #909 (in prod via
 * #928). A grooming row gets it only on the Outstanding list, where the day
 * the charge lands on IS the point; on the day list grooming keeps its
 * previous shape and the server records now.
 */
export function correctedCheckoutVisible(view: OutstandingView, isGrooming: boolean): boolean {
  return !isGrooming || view === 'outstanding';
}

/**
 * The instant to SEND for a corrected date field, as an ISO string — or
 * `null` (⇒ the server stamps now, the unchanged behaviour) when the
 * control carrying the value is NOT on screen, when it is empty, or when it
 * does not parse.
 *
 * `visible` comes from `correctedArrivalVisible` / `correctedCheckoutVisible`
 * — the same call the render makes.
 */
export function correctedInstantToSend(
  visible: boolean,
  raw: string | undefined | null,
): string | null {
  if (!visible) return null;
  if (!raw) return null;
  const at = new Date(raw);
  if (Number.isNaN(at.getTime())) return null;
  return at.toISOString();
}

/**
 * The earliest arrival the corrected-arrival field will offer, as a
 * `datetime-local` `min`: the day BEFORE the visit's first scheduled day.
 *
 * 09-16 re-review, the minor with a consequence: `checkInBooking` bounded
 * the corrected arrival only from ABOVE, so a desk typo (`2025` for `2026`,
 * or a four-digit year that slips a digit) passed every guard, landed in
 * `checked_in_at` verbatim, moved the visit into that day's end-of-day
 * report — and, because `checkOutBooking`'s only floor is the check-in,
 * silently widened what the DEPARTURE would accept. The server now refuses
 * it (`checkin_time_invalid` / `before_booking`); this is the same bound
 * expressed in the control, so the picker will not offer the bad day.
 *
 * One day of slack, not zero: `@db.Date` columns come back as UTC midnight
 * while the desk types a LOCAL instant, and east of UTC a legitimate local
 * arrival on the service day is earlier than that UTC midnight.
 *
 * Returns `''` for a row with no usable date — no bound rather than an
 * invented one.
 */
export function outstandingArrivalMin(
  b: Pick<AdminBooking, 'date' | 'start_date'>,
): string {
  const day = firstServiceDayIso(b);
  if (!/^\d{4}-\d{2}-\d{2}$/.test(day)) return '';
  const floor = new Date(`${day}T00:00:00.000Z`);
  if (Number.isNaN(floor.getTime())) return '';
  floor.setUTCDate(floor.getUTCDate() - 1);
  return `${floor.toISOString().slice(0, 10)}T00:00`;
}
